'use client';

import { useState, useEffect, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { storage } from '@/lib/storage';
import HudPanel, { type HudSeverity } from '@/components/ui/HudPanel';

interface CollapsibleSectionProps {
  sectionKey: string;
  title: string;
  icon?: ReactNode;
  defaultOpen?: boolean;
  /** When true, skip the HudPanel wrapper (use when children have their own panel styling) */
  bare?: boolean;
  /** Severity for the HudPanel chrome — defaults to 'slate'. */
  severity?: HudSeverity;
  /** Optional code label shown in the panel header right slot. */
  code?: string;
  children: ReactNode;
}

export default function CollapsibleSection({
  sectionKey,
  title,
  icon,
  defaultOpen = true,
  bare = false,
  severity = 'slate',
  code,
  children,
}: CollapsibleSectionProps) {
  const storageKey = `dashboard-collapse-${sectionKey}`;

  // Initialize from defaultOpen on BOTH server and client — same value =
  // no hydration mismatch. localStorage gets read in useEffect after
  // mount and overrides if a different preference was persisted.
  // Previously this used a typeof-window branch in useState's lazy
  // initializer, which gave different values server vs client and
  // produced React #300 when the stored preference differed from
  // defaultOpen.
  const [isOpen, setIsOpen] = useState<boolean>(defaultOpen);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    const stored = storage.get(storageKey);
    if (stored !== null) {
      setIsOpen(stored === 'true');
    }
    setHydrated(true);
  }, [storageKey]);

  useEffect(() => {
    if (!hydrated) return;
    storage.set(storageKey, String(isOpen));
  }, [isOpen, storageKey, hydrated]);

  const header = (
    <button
      onClick={() => setIsOpen(prev => !prev)}
      className="w-full flex items-center justify-between gap-2 group"
    >
      <div className="flex items-center gap-2">
        {icon}
        <h2 className="font-heading text-base font-bold uppercase text-white">
          {title}
        </h2>
      </div>
      <ChevronDown
        className={`w-4 h-4 text-gray-500 transition-transform duration-200 group-hover:text-gray-300 ${
          isOpen ? '' : '-rotate-90'
        }`}
      />
    </button>
  );

  // Bare mode: render without HudPanel chrome (the child has its own panel).
  if (bare) {
    return (
      <div>
        {header}
        {isOpen && <div className="mt-3">{children}</div>}
      </div>
    );
  }

  return (
    <HudPanel variant="standard" severity={severity} badge={title.toUpperCase()} code={code ?? ''}>
      <button
        onClick={() => setIsOpen(prev => !prev)}
        className="w-full flex items-center justify-end gap-2 group -mt-1 mb-1"
      >
        <ChevronDown
          className={`w-4 h-4 text-gray-500 transition-transform duration-200 group-hover:text-gray-300 ${
            isOpen ? '' : '-rotate-90'
          }`}
        />
      </button>
      {isOpen && <div>{children}</div>}
    </HudPanel>
  );
}
